/**
 * Site time conventions
 *
 * - **Database**: store absolute instants as UTC ISO strings / PostgreSQL TIMESTAMPTZ.
 * - **Recurring wall times** (lesson hours, weekly availability): store TIME strings as
 *   Central wall clock (no timezone suffix) and convert with `siteDateTime`.
 * - **Display**: always format UTC instants in `America/Chicago` (CST/CDT).
 * - **Calendar date keys** (`YYYY-MM-DD`): always mean a date in Central Time.
 */

import { bosnianLongMonths, intlLocaleFor, toAppLocale, type AppLocale } from "@/lib/locale";

export const SITE_TIMEZONE = "America/Chicago";

/** Resolve BCP 47 tag for member-facing Intl formatters. Defaults to en-US. */
function resolveDisplayLocale(locale?: AppLocale | string): string {
  if (locale == null || locale === "") return intlLocaleFor("en");
  const app = toAppLocale(locale);
  if (app) return intlLocaleFor(app);
  return locale;
}

/**
 * Long-month output fixes: bs formats through a stand-in Intl locale (Serbian jun/jul/avgust),
 * and ICU writes vi «19 tháng 5, 2026» where the natural form is «19 tháng 5 năm 2026».
 */
function longMonthText(text: string, locale?: AppLocale | string): string {
  if (locale == null || locale === "") return text;
  const app = toAppLocale(locale);
  if (app === "bs") return bosnianLongMonths(text);
  if (app === "vi") return text.replace(/(tháng \d{1,2}), (\d{4})/gu, "$1 năm $2");
  // ICU's ms cross-month range adds a comma before the year («30 Jun – 2 Julai, 2026»);
  // ms dates never take one («30 Jun 2026»).
  if (app === "ms") return text.replace(/(\p{L}), (\d{4})/gu, "$1 $2");
  return text;
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

type WallParts = { y: number; mo: number; d: number; h: number; mi: number };

/**
 * `hour12: false` is NOT runtime-stable: some ICU/V8 builds (Node 20, older browsers) resolve
 * it to `hourCycle: "h24"` and format midnight as hour "24" of the *same* day, which shifted
 * every Central-midnight conversion back one day. Always request `hourCycle: "h23"` and
 * normalize a stray "24" defensively so the math never depends on the runtime's ICU data.
 */
const WALL_PARTS_OPTIONS = {
  hourCycle: "h23",
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
} as const satisfies Intl.DateTimeFormatOptions;

const wallFormatters = new Map<string, Intl.DateTimeFormat>();

function wallFormatter(timeZone: string): Intl.DateTimeFormat {
  let fmt = wallFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat("en-US", { ...WALL_PARTS_OPTIONS, timeZone });
    wallFormatters.set(timeZone, fmt);
  }
  return fmt;
}

/** Normalize an ICU hour part: "24" (h24 midnight) means hour 0 of the same calendar day. */
function normalizeHour(hour: number): number {
  return hour === 24 ? 0 : hour;
}

function wallTimeInZone(utcMs: number, timeZone: string): WallParts {
  const parts = wallFormatter(timeZone).formatToParts(new Date(utcMs));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value ?? 0);
  return {
    y: get("year"),
    mo: get("month"),
    d: get("day"),
    h: normalizeHour(get("hour")),
    mi: get("minute"),
  };
}

function wallPartsToUtcMs(parts: WallParts): number {
  return Date.UTC(parts.y, parts.mo - 1, parts.d, parts.h, parts.mi, 0);
}

/** Current instant (same as `new Date()` — wall-clock comparisons use UTC ms internally). */
export function siteNow(): Date {
  return new Date();
}

/** Serialize a Date for TIMESTAMPTZ columns (always UTC). */
export function toUtcIso(instant: Date): string {
  return instant.toISOString();
}

/** Parse a UTC ISO string from the database. */
export function fromUtcIso(iso: string): Date {
  return new Date(iso);
}

/** Convert a Central calendar date + wall time to a UTC instant for storage. */
export function siteDateTime(dateStr: string, time: string): Date {
  if (!DATE_KEY.test(dateStr)) {
    throw new Error(`Invalid date string: ${dateStr}`);
  }
  const [y, mo, d] = dateStr.split("-").map(Number);
  const [h, mi] = time.slice(0, 5).split(":").map(Number);
  const desiredMs = Date.UTC(y, mo - 1, d, h, mi, 0);

  let utcMs = desiredMs;
  for (let i = 0; i < 2; i++) {
    const wall = wallTimeInZone(utcMs, SITE_TIMEZONE);
    utcMs += desiredMs - wallPartsToUtcMs(wall);
  }
  return new Date(utcMs);
}

/** Map a UTC instant to a Central calendar date key (`YYYY-MM-DD`). */
export function siteDateKeyFromInstant(instant: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: SITE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}

export function siteDayBounds(dateStr: string): { start: Date; end: Date } {
  const start = siteDateTime(dateStr, "00:00");
  const nextDay = addDaysToDateKey(dateStr, 1);
  const end = new Date(siteDateTime(nextDay, "00:00").getTime() - 1);
  return { start, end };
}

/** UTC ISO range for one Central calendar day `[start, end)`. */
export function siteDayRange(dateKey: string): { start: string; end: string } {
  const next = addDaysToDateKey(dateKey, 1);
  return {
    start: toUtcIso(siteDateTime(dateKey, "00:00")),
    end: toUtcIso(siteDateTime(next, "00:00")),
  };
}

export function siteDayOfWeek(dateStr: string): number {
  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: SITE_TIMEZONE,
    weekday: "short",
  }).format(siteDateTime(dateStr, "12:00"));
  const map: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  return map[weekday] ?? 0;
}

export function siteTodayKey(now: Date = siteNow()): string {
  return siteDateKeyFromInstant(now);
}

/** Central calendar date keys covered by a UTC range (FullCalendar `datesSet` uses exclusive end). */
export function siteDateKeysFromRange(range: { start: string; end: string }): string[] {
  const startKey = siteDateKeyFromInstant(fromUtcIso(range.start));
  const endKey = siteDateKeyFromInstant(new Date(fromUtcIso(range.end).getTime() - 1));
  return eachSiteDateInRange(startKey, endKey);
}

/** Sunday-start week bounds as UTC ISO instants for a known week-start date key. */
export function siteWeekUtcRange(weekStartSunday: string): { start: string; end: string } {
  const weekEnd = addDaysToDateKey(weekStartSunday, 7);
  return {
    start: toUtcIso(siteDateTime(weekStartSunday, "00:00")),
    end: toUtcIso(siteDateTime(weekEnd, "00:00")),
  };
}

/** Sunday-start week range as UTC ISO instants (matches FullCalendar week view). */
export function siteWeekRangeSunStart(now: Date = siteNow()): { start: string; end: string } {
  const today = siteDateKeyFromInstant(now);
  const weekStart = addDaysToDateKey(today, -siteDayOfWeek(today));
  return siteWeekUtcRange(weekStart);
}

/** Monday-start week range as UTC ISO instants. */
export function siteWeekRangeMonStart(anchorKey: string): { start: string; end: string } {
  const weekStart = siteMondayStartKey(anchorKey);
  const weekEnd = addDaysToDateKey(weekStart, 7);
  return {
    start: toUtcIso(siteDateTime(weekStart, "00:00")),
    end: toUtcIso(siteDateTime(weekEnd, "00:00")),
  };
}

/** Central date key for the Sunday of the week containing `anchorKey`. */
export function siteSundayStartKey(anchorKey: string): string {
  return addDaysToDateKey(anchorKey, -siteDayOfWeek(anchorKey));
}

/** Central date key for the Monday of the week containing `anchorKey`. */
function siteMondayStartKey(anchorKey: string): string {
  const dow = siteDayOfWeek(anchorKey);
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  return addDaysToDateKey(anchorKey, mondayOffset);
}

/** Earliest end_at still shown on the public events page (completed events drop off after 6 months). */
export function publishedSiteEventEndCutoff(now: Date = siteNow()): Date {
  const siteKey = siteDateKeyFromInstant(now);
  const [y, mo, d] = siteKey.split("-").map(Number);
  let month = mo - 6;
  let year = y;
  while (month <= 0) {
    month += 12;
    year -= 1;
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  return siteDateTime(
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    "00:00",
  );
}

/**
 * Map a day-picker cell to a Central calendar date key.
 * Uses the picker cell's local Y-M-D (the day number shown in the grid).
 */
export function calendarSelectionToSiteKey(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/**
 * Month bounds for a day-picker calendar month.
 * Uses the picker's local Y-M-D (same as `calendarSelectionToSiteKey`) so UTC hosts
 * do not shift first-of-month midnight into the prior Central month.
 */
export function siteMonthBounds(calendarMonth: Date): { start: string; end: string } {
  const siteKey = calendarSelectionToSiteKey(calendarMonth);
  const [y, m] = siteKey.split("-").map(Number);
  const start = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const end = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { start, end };
}

export function addDaysToDateKey(dateStr: string, days: number): string {
  const anchor = siteDateTime(dateStr, "12:00");
  return siteDateKeyFromInstant(new Date(anchor.getTime() + days * 86_400_000));
}

export function eachSiteDateInRange(start: string, end: string): string[] {
  const dates: string[] = [];
  let cur = start;
  while (cur <= end) {
    dates.push(cur);
    cur = addDaysToDateKey(cur, 1);
  }
  return dates;
}

export function compareUtcIso(a: string, b: string): number {
  return fromUtcIso(a).getTime() - fromUtcIso(b).getTime();
}

export function isUtcIsoBefore(iso: string, now: Date = siteNow()): boolean {
  return fromUtcIso(iso).getTime() < now.getTime();
}

export function isUtcIsoAfter(iso: string, now: Date = siteNow()): boolean {
  return fromUtcIso(iso).getTime() >= now.getTime();
}

/**
 * Wall-clock time in Central. The hour cycle follows the display locale (en-US 12h,
 * ru/de/fi… 24h); callers without a locale get en-US, so staff views keep "9:00 AM".
 */
export function formatSiteTime(iso: string, locale?: AppLocale | string): string {
  return fromUtcIso(iso).toLocaleTimeString(resolveDisplayLocale(locale), {
    hour: "numeric",
    minute: "2-digit",
    timeZone: SITE_TIMEZONE,
  });
}

/**
 * Localized slot range in Central, e.g. "9:00 – 9:30 AM" (en), "9:00–9:30" (ru),
 * "오전 9:00~9:30" (ko). Uses `Intl.DateTimeFormat#formatRange` so the range separator
 * and shared day-period come from the locale instead of a hard-coded English "to".
 * The fixed `timeZone` keeps SSR (UTC host) and browser output identical.
 */
export function formatSiteTimeRange(
  startIso: string,
  endIso: string,
  locale?: AppLocale | string,
): string {
  const fmt = new Intl.DateTimeFormat(resolveDisplayLocale(locale), {
    hour: "numeric",
    minute: "2-digit",
    timeZone: SITE_TIMEZONE,
  });
  const start = fromUtcIso(startIso);
  const end = fromUtcIso(endIso);
  if (typeof fmt.formatRange === "function") return fmt.formatRange(start, end);
  return `${fmt.format(start)} – ${fmt.format(end)}`;
}

export function formatSiteDateLabel(dateStr: string, locale?: AppLocale | string): string {
  return longMonthText(
    siteDateTime(dateStr, "12:00").toLocaleDateString(resolveDisplayLocale(locale), {
      weekday: "long",
      month: "long",
      day: "numeric",
      timeZone: SITE_TIMEZONE,
    }),
    locale,
  );
}

/** Short label for a Central date key, e.g. "Wed, Jun 11". */
export function formatSiteDateKeyShort(dateStr: string): string {
  return siteDateTime(dateStr, "12:00").toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: SITE_TIMEZONE,
  });
}

/**
 * Booking cutoff wall time (a Central `HH:MM` TIME value), e.g. "6 PM" (en), "18:00" (ru),
 * "18.00" (fi). Without a locale it stays en-US, which the server's English error copy uses.
 * 12-hour locales drop ":00" on the hour ("6 PM"); 24-hour locales always show minutes.
 * The fixed `timeZone` keeps the SSR host (UTC) and browsers identical.
 */
export function formatSiteCutoffTime(time: string, locale?: AppLocale | string): string {
  const [h, m] = time.slice(0, 5).split(":").map(Number);
  const intl = resolveDisplayLocale(locale);
  const cycle = new Intl.DateTimeFormat(intl, {
    hour: "numeric",
    timeZone: SITE_TIMEZONE,
  }).resolvedOptions().hourCycle;
  const twentyFourHour = cycle === "h23" || cycle === "h24";
  return siteDateTime(
    "2000-01-01",
    `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`,
  ).toLocaleTimeString(intl, {
    hour: "numeric",
    minute: m === 0 && !twentyFourHour ? undefined : "2-digit",
    timeZone: SITE_TIMEZONE,
  });
}

/** e.g. "Wed, Jun 11 · 9:00 AM" */
export function formatSiteDateTime(iso: string, locale?: AppLocale | string): string {
  const intl = resolveDisplayLocale(locale);
  const datePart = fromUtcIso(iso).toLocaleDateString(intl, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: SITE_TIMEZONE,
  });
  return `${datePart} · ${formatSiteTime(iso, locale)}`;
}

/** e.g. "Jun 11, 2026" — date only, no time */
export function formatSiteDateOnly(iso: string): string {
  return fromUtcIso(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: SITE_TIMEZONE,
  });
}

/** e.g. "June 11, 2026" — date only, long month */
export function formatSiteDateLong(iso: string, locale?: AppLocale | string): string {
  return longMonthText(
    fromUtcIso(iso).toLocaleDateString(resolveDisplayLocale(locale), {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: SITE_TIMEZONE,
    }),
    locale,
  );
}

/**
 * Calendar span of an event in Central, localized: "May 21 – 24, 2026" (en),
 * "21.–24. Mai 2026" (de), "21.–24. maj 2026." (bs), «2026年5月21日～24日» (ja),
 * «2026年5月21日至24日» (zh). A start and end on the same Central day give one date
 * ("June 9, 2026"). Uses `Intl.DateTimeFormat#formatRange` so shared month/year parts and
 * the range dash come from the locale; the fixed `timeZone` keeps SSR (UTC host) and browser
 * output identical.
 */
export function formatSiteDateRange(
  startIso: string,
  endIso: string,
  locale?: AppLocale | string,
): string {
  const app = locale == null || locale === "" ? "en" : toAppLocale(locale);
  const fmt = new Intl.DateTimeFormat(resolveDisplayLocale(locale), {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: SITE_TIMEZONE,
  });
  const start = fromUtcIso(startIso);
  const end = fromUtcIso(endIso);
  const startKey = siteDateKeyFromInstant(start);
  const endKey = siteDateKeyFromInstant(end);
  if (!(end.getTime() > start.getTime()) || startKey === endKey) {
    return longMonthText(fmt.format(start), locale);
  }
  const [sy, sm, sd] = startKey.split("-").map(Number);
  const [ey, em, ed] = endKey.split("-").map(Number);

  // ICU has no long-form interval pattern for zh/ja (it falls back to "2026/5/21 – 2026/5/24").
  // Write the native form, repeating only the parts that change.
  if (app === "ja" || app === "zh") {
    const tail = sy !== ey ? `${ey}年${em}月${ed}日` : sm !== em ? `${em}月${ed}日` : `${ed}日`;
    return `${sy}年${sm}月${sd}日${app === "ja" ? "～" : "至"}${tail}`;
  }

  const separate = `${fmt.format(start)} – ${fmt.format(end)}`;
  let text = separate;
  if (typeof fmt.formatRange === "function") {
    const range = fmt.formatRange(start, end);
    // ICU range data is uneven: sw drops the start month across months ("30 – 2 Juni 2026")
    // and cs goes all-numeric. Keep the range only when it still names the start month the
    // way the single-date format does ("Mai", "5월").
    const parts = fmt.formatToParts(start);
    const i = parts.findIndex((part) => part.type === "month");
    // CJK month numbers carry their unit flush ("5" + "월"); spaced literals (" de ") do not.
    const next = parts[i + 1]?.type === "literal" ? parts[i + 1].value : "";
    const startMonth = i < 0 ? null : parts[i].value + (/^\p{L}/u.test(next) ? next[0] : "");
    if (startMonth && range.includes(startMonth)) {
      text = range;
    } else if (app === "cs" && sy === ey && sm === em) {
      // cs: «21.–24. května 2026», not the long «21. května 2026 – 24. května 2026».
      text = `${sd}.–${fmt.format(end)}`;
    } else if (app === "cs" && sy === ey) {
      // cs cross-month: «30. června – 2. července 2026», the year once.
      const dayMonth = new Intl.DateTimeFormat(resolveDisplayLocale(locale), {
        month: "long",
        day: "numeric",
        timeZone: SITE_TIMEZONE,
      });
      text = `${dayMonth.format(start)} – ${fmt.format(end)}`;
    }
  }
  // ICU zero-pads the end day in some cross-month ranges («30. maj – 02. jun», «02 giugno»).
  if (app === "bs" || app === "it") text = text.replace(/(?<!\d)0(\d)(?!\d)/gu, "$1");
  return longMonthText(text, locale);
}

/** e.g. "June 2026" — month and year */
export function formatSiteMonthYear(iso: string, locale?: AppLocale | string): string {
  return longMonthText(
    fromUtcIso(iso).toLocaleDateString(resolveDisplayLocale(locale), {
      month: "long",
      year: "numeric",
      timeZone: SITE_TIMEZONE,
    }),
    locale,
  );
}

/** e.g. "Jun 11, 2026 · 9:00 AM" */
export function formatSiteDateTimeLong(iso: string, locale?: AppLocale | string): string {
  const intl = resolveDisplayLocale(locale);
  const datePart = fromUtcIso(iso).toLocaleDateString(intl, {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: SITE_TIMEZONE,
  });
  return `${datePart} · ${formatSiteTime(iso, locale)}`;
}

/** e.g. "Wed Jun 11, 9:00 AM" — for event dialogs */
export function formatSiteEventStart(iso: string): string {
  return fromUtcIso(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: SITE_TIMEZONE,
    hour12: true,
  });
}

/** Format UTC instant as datetime-local input value in Central Time (YYYY-MM-DDTHH:mm). */
export function formatSiteDatetimeLocal(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SITE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    // Not `hour12: false` — see WALL_PARTS_OPTIONS (h24 renders midnight as "24").
    hourCycle: "h23",
  }).formatToParts(fromUtcIso(iso));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  const hour = String(normalizeHour(Number(get("hour")))).padStart(2, "0");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}

/** Parse datetime-local value as Central wall time → UTC ISO string for storage. */
export function siteDatetimeLocalToInstant(local: string): string {
  const [datePart, timePart] = local.split("T");
  if (!datePart || !timePart) throw new Error(`Invalid datetime-local value: ${local}`);
  const time = timePart.slice(0, 5);
  return toUtcIso(siteDateTime(datePart, time));
}

/**
 * FullCalendar with a named timeZone (no tz plugin) uses UTC-coercion: grid times are
 * read/written via getUTC* on Date objects, and ISO strings with `Z` are NOT shifted.
 * Use these helpers at the FC boundary; keep siteDateTime/toUtcIso for the database.
 * Also pass initialDate={siteTodayKey()} so the opening week follows Central, not host TZ.
 */

/** Site date key from a FullCalendar Date (read getUTC* fields, not local/browser). */
export function siteDateKeyFromCalendarDate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** FC event start/end from a Central calendar date + wall time (TIME column or grid row). */
export function siteCalendarWallDateTime(dateKey: string, time: string): string {
  const [h, mi] = time.slice(0, 5).split(":").map(Number);
  return `${dateKey}T${String(h).padStart(2, "0")}:${String(mi).padStart(2, "0")}:00`;
}

/** FC event start/end from a DB UTC instant — maps to Central wall on the grid. */
export function utcIsoToCalendarDateTime(iso: string): string {
  return `${formatSiteDatetimeLocal(iso)}:00`;
}

/** Persist a FullCalendar interaction Date as a real UTC ISO string for TIMESTAMPTZ. */
export function calendarDateToUtcIso(d: Date): string {
  const dateKey = siteDateKeyFromCalendarDate(d);
  const time = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
  return toUtcIso(siteDateTime(dateKey, time));
}

/** e.g. "Jun 11, 9:00 AM – 5:00 PM" for event datetime ranges */
export function formatSiteEventRange(startIso: string, endIso: string): string {
  const startDate = siteDateKeyFromInstant(fromUtcIso(startIso));
  const endDate = siteDateKeyFromInstant(fromUtcIso(endIso));
  if (startDate === endDate) {
    return `${formatSiteDateTimeLong(startIso).replace(/ · .+$/, "")} · ${formatSiteTime(startIso)} – ${formatSiteTime(endIso)}`;
  }
  return `${formatSiteDateTimeLong(startIso)} – ${formatSiteDateTimeLong(endIso)}`;
}

/** Label for a Monday-start week in Central Time. */
function formatSiteWeekRangeLabel(weekStartKey: string): string {
  const weekEndKey = addDaysToDateKey(weekStartKey, 6);
  const startLabel = siteDateTime(weekStartKey, "12:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: SITE_TIMEZONE,
  });
  const endLabel = siteDateTime(weekEndKey, "12:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: SITE_TIMEZONE,
  });
  return `${startLabel} – ${endLabel}`;
}
